/**
 * Usage Radial Card Component
 *
 * Displays a usage metric with a radial progress indicator.
 * Uses SVG for reliable cross-browser rendering.
 */

import type { LucideIcon } from 'lucide-react';
import { Card, CardContent } from '@loader/ui';

import {
  USAGE_WARNING_THRESHOLD,
  USAGE_CRITICAL_THRESHOLD,
} from '../constants';

interface UsageRadialCardProps {
  label: string;
  icon: LucideIcon;
  usage: number;
  limit: number | null;
  percentUsed: number | null;
  formatValue: (value: number) => string;
}

/**
 * Get color class based on usage percentage
 */
function getUsageColorClass(percent: number): string {
  if (percent >= USAGE_CRITICAL_THRESHOLD) {
    return 'stroke-destructive';
  }
  if (percent >= USAGE_WARNING_THRESHOLD) {
    return 'stroke-amber-500';
  }
  return 'stroke-primary';
}

export function UsageRadialCard({
  label,
  icon: Icon,
  usage,
  limit,
  percentUsed,
  formatValue,
}: UsageRadialCardProps) {
  const percent = percentUsed ?? 0;
  const cappedPercent = Math.min(percent, 100);
  const colorClass = getUsageColorClass(percent);

  // SVG circle parameters
  const size = 88;
  const strokeWidth = 7;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (cappedPercent / 100) * circumference;

  return (
    <Card>
      <CardContent className="flex flex-col items-center px-3 pt-2 pb-2">
        {/* Radial progress */}
        <div className="relative mb-3">
          <svg width={size} height={size} className="-rotate-90">
            {/* Background circle */}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              strokeWidth={strokeWidth}
              className="stroke-muted"
            />
            {/* Progress circle */}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              className={colorClass}
            />
          </svg>
          {/* Center text */}
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-lg font-semibold">
              {Math.round(percent)}%
            </span>
          </div>
        </div>

        {/* Label with icon */}
        <div className="flex items-center gap-2 font-medium">
          <Icon className="h-4 w-4 text-muted-foreground" />
          {label}
        </div>

        {/* Usage details */}
        <p className="mt-0.5 text-sm text-muted-foreground">
          {formatValue(usage)} / {limit !== null ? formatValue(limit) : '∞'}
        </p>
      </CardContent>
    </Card>
  );
}
